import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Flex, Table, Tooltip } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import _ from "lodash";

import {
  FIELD_LABELS,
  FIELD_ORDERS,
  FIELD_KEYS,
  TABLE_FIELDS,
  DETECTED_CODE_TYPE_COLOR_MAP,
} from "constants/UnitFields";
import { encodeQueryParamsWithProducts } from "functions/queryParamFuncs";
import { StringParam } from "use-query-params";
import { useProduct } from "hooks/useProduct";
import CollapsibleText from "components/Common/CollapsibleText";
import StatusTag from "components/Common/StatusTag";
import UnitDetailsModal from "components/ItemDetails/UnitDetailsModal";

const columnAdditionalConfig = {
  [FIELD_KEYS.DETECTED_CODE]: {
    align: "left",
    width: "10rem",
  },
};

const UnitTable = ({ tableData, loading }) => {
  const navigate = useNavigate();
  const { selectedProductIds } = useProduct();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewUnitIndex, setViewUnitIndex] = useState();

  const columns = _.intersection(FIELD_ORDERS, TABLE_FIELDS).map((field) => ({
    title: FIELD_LABELS[field],
    dataIndex: field,
    align: "center",
    render: (value, index, row) => renderField(field, value, index, row),
    ...(columnAdditionalConfig[field] ?? {}),
  }));

  const navigateToErrorList = (itemRefNo) => {
    return navigate({
      pathname: "/errors",
      search: `?${encodeQueryParamsWithProducts(
        selectedProductIds,
        { search: StringParam },
        { search: itemRefNo }
      )}`,
    });
  };

  const renderField = (field, val, row, index) => {
    switch (field) {
      case FIELD_KEYS.NO:
        return index + 1;
      case FIELD_KEYS.STATUS:
        return (
          <Flex justify="center" align="center">
            <StatusTag status={val} />
            {!!row.has_pending_action && (
              <Tooltip title="View Error">
                <Button
                  onClick={() => navigateToErrorList(row.item_ref_no)}
                  type="text"
                  icon={<WarningOutlined style={{ color: "#faad14" }} />}
                />
              </Tooltip>
            )}
          </Flex>
        );
      case FIELD_KEYS.DETECTED_CODE:
        const fieldVal = val?.value ?? "-";
        const fieldColor = !!val?.type
          ? DETECTED_CODE_TYPE_COLOR_MAP[val.type]
          : null;
        const fieldStyle = !!fieldColor
          ? {
              color: fieldColor,
            }
          : {};

        return (
          <CollapsibleText
            content={fieldVal}
            width={columnAdditionalConfig[field]?.width}
            style={fieldStyle}
          />
        );
      case FIELD_KEYS.ACTION:
        return (
          <Button type="link" onClick={() => viewDetails(index)}>
            View
          </Button>
        );
      default:
        return val ?? "-";
    }
  };

  const viewDetails = (index) => {
    setViewUnitIndex(index);
    setShowDetailsModal(true);
  };

  const closeModal = () => {
    setViewUnitIndex();
    setShowDetailsModal(false);
  };

  return (
    <>
      <Table
        columns={columns}
        dataSource={tableData}
        rowKey={(record) => record[FIELD_KEYS.X]}
        loading={loading}
        pagination={false}
        size="middle"
      />
      <UnitDetailsModal
        key={"unit_" + viewUnitIndex}
        showModal={showDetailsModal}
        unitIndex={viewUnitIndex}
        unitListData={tableData}
        onClose={() => closeModal()}
      />
    </>
  );
};

export default UnitTable;
